import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

const createProviderRequest = async (
  props: paths['/provider']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/provider']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/provider']['post']['responses']['201']['content']['application/json']
    >
  >(`/provider`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateProviderMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['provider'],
    mutationFn: (
      props: paths['/provider']['post']['requestBody']['content']['application/json'],
    ) => createProviderRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['providers'] }),
  });

  return mutation;
};

const editProviderRequest = async (
  props: paths['/provider/{record_id}']['patch']['requestBody']['content']['application/json'] & {
    provider_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/provider/{record_id}']['patch']['requestBody']['content']['application/json']
  >(`/provider/${props.provider_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditProviderMutation = (provider_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editProvider'],
    mutationFn: (
      props: paths['/provider/{record_id}']['patch']['requestBody']['content']['application/json'] & {
        provider_id: string;
      },
    ) => editProviderRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['providers'] }),
        client.invalidateQueries({
          queryKey: ['provider', provider_id],
        }),
        client.invalidateQueries({
          queryKey: ['patientCareTeam'],
        }),
      ]),
  });

  return mutation;
};

export const providersCsvDownloadRequest = async (
  props: paths['/record/providers/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/providers/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const useProvidersCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['providersCSVDownload'],
    mutationFn: (
      props: paths['/record/providers/csv']['get']['parameters']['query'],
    ) => providersCsvDownloadRequest(props),
  });

  return mutation;
};

export const providerCsvTemplateDownloadRequest = async (record_id: string) => {
  const response = await axiosInstance.get(`/provider/template.csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: {
      ...(record_id && { record_id }),
    },
  });

  const data = (await response).data;

  return data;
};

export const useProviderCsvTemplateDownloadMutation = () => {
  const mutation = useMutation({
    mutationKey: ['providerCSVTemplateDownload'],
    mutationFn: (record_id: string) =>
      providerCsvTemplateDownloadRequest(record_id),
  });

  return mutation;
};

export const providerCsvImportRequest = async (
  record_id: string,
  file: FormData,
) => {
  const response = await axiosInstance.post(
    `/practice/${record_id}/providers/csv`,
    file,
    {
      headers: {
        'Content-type': 'application/xml',
      },
    },
  );

  const data = (await response).data;

  return data;
};

export const useProvidersCsvImportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['providerCSVImport'],
    mutationFn: ({ file, record_id }: { file: FormData; record_id: string }) =>
      providerCsvImportRequest(record_id, file),
  });

  return mutation;
};
